// src/pages/Availability.js
import React, { useCallback, useContext, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { LeagueContext } from "../context/LeagueContext";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  query,
  setDoc,
  updateDoc,
  where,
} from "firebase/firestore";
import styled from "styled-components";

import { db } from "../lib/firebase";
import { paths } from "../lib/paths";
import { useAuth } from "../context/AuthContext";
import Panel from "../central/Panel";
import PageLoader from "../components/PageLoader";

// ─── Helpers ──────────────────────────────────────────────────────────────────

/** Extract time as "HH:MM" from a Firestore timestamp or JS Date */
function formatKickoffTime(date) {
  if (!date) return null;
  const d = date instanceof Date ? date : (date.toDate ? date.toDate() : new Date(date));
  const hours = String(d.getHours()).padStart(2, "0");
  const mins = String(d.getMinutes()).padStart(2, "0");
  return `${hours}:${mins}`;
}

/** Format a JS Date as "Sun 7 Jun" — for compact header use */
function formatShortDate(date) {
  if (!date) return "—";
  const d = date instanceof Date ? date : (date.toDate ? date.toDate() : new Date(date));
  const dayName = d.toLocaleString("default", { weekday: "short" });
  return `${dayName} ${d.getDate()} ${d.toLocaleString("default", { month: "short" })}`;
}

/** Format a JS Date as "Saturday 7 Jun" */
function formatMatchDate(date) {
  if (!date) return "—";
  const d = date instanceof Date ? date : (date.toDate ? date.toDate() : new Date(date));
  const dayName = d.toLocaleString("default", { weekday: "short" });
  const dayNum = d.getDate();
  const month = d.toLocaleString("default", { month: "short" });
  return `${dayName} ${dayNum} ${month}`;
}

// ─── Styled components ────────────────────────────────────────────────────────

const PageWrapper = styled.div`
  max-width: 600px;
  margin: 0 auto;
  padding: ${({ theme }) => theme.spacing.medium};
  padding-bottom: 80px;
`;

const PageHeadline = styled.h1`
  font-family: ${({ theme }) => theme.typography.fontFamily.heading};
  font-size: ${({ theme }) => theme.typography.fontSize.heading};
  font-weight: ${({ theme }) => theme.typography.fontWeight.heading};
  color: ${({ theme }) => theme.colors.text};
  margin: 0 0 ${({ theme }) => theme.spacing.xsmall};
`;

const MatchMeta = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.body};
  color: ${({ theme }) => theme.colors.textLight};
  margin: 0 0 ${({ theme }) => theme.spacing.large};
`;

const GWCard = styled(Panel)`
  max-width: 100%;
  margin-bottom: ${({ theme }) => theme.spacing.medium};
`;

const GWCardHeader = styled.div`
  display: flex;
  align-items: baseline;
  gap: ${({ theme }) => theme.spacing.medium};
  margin-bottom: ${({ theme }) => theme.spacing.medium};
`;

const GWLabel = styled.span`
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  font-size: ${({ theme }) => theme.typography.fontSize.large};
  color: ${({ theme }) => theme.colors.text};
`;

const GWMeta = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  color: ${({ theme }) => theme.colors.textLight};
`;

const ButtonRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${({ theme }) => theme.spacing.medium};
`;

const AvailBtn = styled.button`
  padding: 1rem;
  border-radius: 10px;
  border: none;
  width: 100%;
  font-family: ${({ theme }) => theme.typography.fontFamily.body};
  font-size: ${({ theme }) => theme.typography.fontSize.body};
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  cursor: pointer;
  transition: opacity 150ms ease;
  background: ${({ $variant }) => $variant === "available" ? "#50B4C8" : "#f0f0f0"};
  color: ${({ $variant }) => $variant === "available" ? "#ffffff" : "#333333"};

  &:hover { opacity: 0.85; }
  &:active { opacity: 0.7; }
`;

const ResponseBadge = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.small};
  margin-bottom: ${({ theme }) => theme.spacing.medium};
`;

const ResponseText = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.large};
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  color: ${({ $resp, theme }) =>
    $resp === "available" ? theme.colors.primary : theme.colors.textLight};
`;

const ChangeLink = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.primary};
  text-decoration: underline;
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  cursor: pointer;
  padding: 0;
  font-family: ${({ theme }) => theme.typography.fontFamily.body};
`;

const MemberGroupTitle = styled.h3`
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: ${({ theme }) => theme.colors.textLight};
  margin: ${({ theme }) => theme.spacing.medium} 0 ${({ theme }) => theme.spacing.small};
`;

const MemberList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
`;

const MemberItem = styled.li`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.small};
  padding: ${({ theme }) => theme.spacing.xsmall} 0;
  font-size: ${({ theme }) => theme.typography.fontSize.body};
  color: ${({ theme }) => theme.colors.text};
`;

const Dot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
  background: ${({ $color }) => $color || "#cccccc"};
`;

/* Small tag marking players without a ClubWeek account */
const GuestTag = styled.span`
  font-size: 0.65rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: ${({ theme }) => theme.colors.textLight};
  background: ${({ theme }) => theme.colors.border};
  padding: 1px 6px;
  border-radius: 10px;
`;

const Divider = styled.hr`
  border: none;
  border-top: 1px solid ${({ theme }) => theme.colors.border};
  margin: ${({ theme }) => `${theme.spacing.large} 0`};
`;

const SectionTitle = styled.h2`
  font-size: ${({ theme }) => theme.typography.fontSize.subheading};
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text};
  margin: 0 0 ${({ theme }) => theme.spacing.medium};
`;

const CompactCard = styled(Panel)`
  max-width: 100%;
  margin-bottom: ${({ theme }) => theme.spacing.small};
  padding: ${({ theme }) => theme.spacing.medium};
`;

const CompactHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing.small};
`;

const CompactLeft = styled.div``;

const CompactButtonRow = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.small};
`;

const SmallBtn = styled.button`
  padding: 6px 14px;
  border-radius: 8px;
  border: none;
  background: ${({ $variant }) => $variant === "available" ? "#50B4C8" : "#f0f0f0"};
  color: ${({ $variant }) => $variant === "available" ? "#ffffff" : "#333333"};
  font-family: ${({ theme }) => theme.typography.fontFamily.body};
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  cursor: pointer;
  transition: opacity 150ms ease;
  &:hover { opacity: 0.8; }
`;

const EmptyState = styled.p`
  color: ${({ theme }) => theme.colors.textLight};
  font-size: ${({ theme }) => theme.typography.fontSize.body};
  padding: ${({ theme }) => theme.spacing.large} 0;
  text-align: center;
`;

// ─── Component ────────────────────────────────────────────────────────────────

export default function Availability() {
  const { leagueID: paramLeagueID } = useParams();
  const { currentLeagueID } = useContext(LeagueContext);
  const leagueID = paramLeagueID || currentLeagueID;
  const { currentUser } = useAuth();

  const [loading, setLoading] = useState(true);
  const [seasonID, setSeasonID] = useState("S1");
  const [totalPlayers, setTotalPlayers] = useState(0);
  const [currentGW, setCurrentGW] = useState(null);

  // Availability responses for current GW: { [uid]: "available" | "unavailable" }
  const [responses, setResponses] = useState({});

  // Whether to show buttons again (after "Change" tapped)
  const [changingGW, setChangingGW] = useState({});

  // League members: [{ uid, displayName, linkedPlayerID }]
  const [members, setMembers] = useState([]);

  // All players in the current season: [{ id, name }] — used to surface
  // unlinked players (those without a ClubWeek account) in the lists too.
  const [allPlayers, setAllPlayers] = useState([]);

  // Calculated upcoming matches: [{ gwID, date }]
  const [upcomingMatches, setUpcomingMatches] = useState([]);

  // Responses for upcoming GWs: { [gwID]: { [uid]: "available"|"unavailable" } }
  const [upcomingResponses, setUpcomingResponses] = useState({});

  const uid = currentUser?.uid;

  // ── Fetch all data ──────────────────────────────────────────────────────────

  const fetchData = useCallback(async () => {
    if (!leagueID || !uid) return;
    setLoading(true);

    try {
      // 1. League doc → seasonID
      const leagueSnap = await getDoc(doc(db, "leagues", leagueID));
      const leagueData = leagueSnap.exists() ? leagueSnap.data() : {};
      const resolvedSeasonID = leagueData.currentSeasonID || "S1";
      setSeasonID(resolvedSeasonID);
      const tp = parseInt(leagueData.leagueType || "0", 10) || 0;
      setTotalPlayers(tp);

      // 2. Current GW (isCurrent === true)
      const gwQuery = query(
        paths.leagueGameweeks(leagueID, resolvedSeasonID),
        where("isCurrent", "==", true),
        limit(1)
      );
      const gwSnap = await getDocs(gwQuery);
      if (gwSnap.empty) {
        setCurrentGW(null);
        setLoading(false);
        return;
      }
      const gwDoc = gwSnap.docs[0];
      const gwData = { id: gwDoc.id, ...gwDoc.data() };
      setCurrentGW(gwData);

      // 3. Availability responses for current GW
      const availRef = doc(
        db,
        "leagues", leagueID,
        "seasons", resolvedSeasonID,
        "gameweeks", gwData.id,
        "availability", "responses"
      );
      const availSnap = await getDoc(availRef);
      setResponses(availSnap.exists() ? (availSnap.data()?.responses || {}) : {});

      // 4. League members
      const usersSnap = await getDocs(
        query(
          collection(db, "users"),
          where("leagueIDs", "array-contains", leagueID)
        )
      );
      setMembers(
        usersSnap.docs.map((d) => ({
          uid: d.id,
          displayName: d.data().displayName || d.data().name || "Unknown",
          linkedPlayerID: d.data().linkedPlayerIDs?.[leagueID] || null,
        }))
      );

      // 4b. All players in the current season (to show unlinked players too)
      try {
        const playersSnap = await getDocs(paths.leaguePlayers(leagueID, resolvedSeasonID));
        setAllPlayers(
          playersSnap.docs.map((d) => ({ id: d.id, name: d.data().name || "Unknown" }))
        );
      } catch (e) {
        console.error("Availability: failed to fetch players", e);
        setAllPlayers([]);
      }

      // 5. Calculate next 5 match dates from current deadline (+7 days each)
      const deadline = gwData.deadline;
      const currentNum = gwData.number || 1;
      const kickoffTime = formatKickoffTime(gwData.deadline);
      const baseDate = deadline?.toDate ? deadline.toDate() : new Date(deadline);
      const calculated = Array.from({ length: 5 }, (_, i) => {
        const date = new Date(baseDate);
        date.setDate(date.getDate() + (i + 1) * 7);
        date.setHours(baseDate.getHours(), baseDate.getMinutes(), 0, 0);
        return { gwID: `GW${currentNum + i + 1}`, date, kickoffTime };
      });
      setUpcomingMatches(calculated);

      // 6. Fetch availability responses for each calculated GW (may not exist yet)
      const upcomingResps = {};
      await Promise.all(
        calculated.map(async ({ gwID }) => {
          const ref = doc(
            db,
            "leagues", leagueID,
            "seasons", resolvedSeasonID,
            "gameweeks", gwID,
            "availability", "responses"
          );
          const snap = await getDoc(ref);
          upcomingResps[gwID] = snap.exists()
            ? (snap.data()?.responses || {})
            : {};
        })
      );
      setUpcomingResponses(upcomingResps);
    } catch (err) {
      console.error("Availability: fetch error", err);
    } finally {
      setLoading(false);
    }
  }, [leagueID, uid]);

  useEffect(() => {
    if (!leagueID || !uid) {
      setLoading(false);
    }
  }, [leagueID, uid]);

  useEffect(() => {
    if (leagueID && uid) {
      fetchData();
    }
  }, [leagueID, uid, fetchData]);

  // ── Write response ──────────────────────────────────────────────────────────

  async function submitResponse(gwID, value) {
    if (!uid || !leagueID || !seasonID) return;
    const availRef = doc(
      db,
      "leagues", leagueID,
      "seasons", seasonID,
      "gameweeks", gwID,
      "availability", "responses"
    );
    try {
      // Try updateDoc first; fall back to setDoc if doc doesn't exist yet
      try {
        await updateDoc(availRef, { [`responses.${uid}`]: value });
      } catch (err) {
        if (err.code === "not-found") {
          await setDoc(availRef, { responses: { [uid]: value } }, { merge: true });
        } else {
          throw err;
        }
      }
      // Optimistically update local state
      if (gwID === currentGW?.id) {
        setResponses((prev) => ({ ...prev, [uid]: value }));
      } else {
        setUpcomingResponses((prev) => ({
          ...prev,
          [gwID]: { ...(prev[gwID] || {}), [uid]: value },
        }));
      }
      setChangingGW((prev) => ({ ...prev, [gwID]: false }));
    } catch (err) {
      console.error("Availability: write error", err);
    }
  }

  // ── Derived state ────────────────────────────────────────────────────────────

  const myCurrentResponse = currentGW ? responses[uid] : null;
  const showButtonsForCurrent = !myCurrentResponse || changingGW[currentGW?.id];

  // Unified roster: ClubWeek members (keyed by uid) + players without a
  // ClubWeek account (keyed by playerID). Unlinked players are those not tied
  // to any member via linkedPlayerIDs — mirrors the ManageLeague availability view.
  const linkedPlayerIDSet = new Set(members.map((m) => m.linkedPlayerID).filter(Boolean));
  const unlinkedPlayers = allPlayers.filter((p) => !linkedPlayerIDSet.has(p.id));
  const roster = [
    ...members.map((m) => ({ key: m.uid, name: m.displayName, resp: responses[m.uid], guest: false })),
    ...unlinkedPlayers.map((p) => ({ key: p.id, name: p.name, resp: responses[p.id], guest: true })),
  ];

  const playing    = roster.filter((r) => r.resp === "available");
  const notPlaying = roster.filter((r) => r.resp === "unavailable");
  const noResponse = roster.filter((r) => !r.resp);

  // Responded = roster entries (members + unlinked players) with a response.
  const respondedCount = playing.length + notPlaying.length;
  const rosterCount = roster.length;

  // ── Render ───────────────────────────────────────────────────────────────────

  if (loading) return <PageLoader message="Loading availability…" />;

  return (
    <PageWrapper>
      <PageHeadline>Who's Playing?</PageHeadline>

      {!currentGW ? (
        <EmptyState>No active gameweek found.</EmptyState>
      ) : (
        <>
          <MatchMeta>
            {formatMatchDate(currentGW.deadline)}
            {formatKickoffTime(currentGW.deadline) ? ` · ${formatKickoffTime(currentGW.deadline)} kickoff` : ""}
          </MatchMeta>

          {/* ── Current GW card ─────────────────────────────────────────── */}
          <GWCard>
            <GWCardHeader>
              <GWLabel>{currentGW.id}</GWLabel>
              <GWMeta>
                {formatShortDate(currentGW.deadline)}
                {formatKickoffTime(currentGW.deadline) ? ` · ${formatKickoffTime(currentGW.deadline)}` : ""}
              </GWMeta>
              {rosterCount > 0 && (
                <span style={{ fontSize: "0.85rem", color: "#888", marginLeft: "auto" }}>
                  {respondedCount}/{rosterCount} responded
                  {" · "}
                  <span style={{ color: playing.length >= totalPlayers && totalPlayers > 0 ? "#2e7d32" : "#e65100", fontWeight: 600 }}>
                    {playing.length}/{totalPlayers} min. required
                  </span>
                  {notPlaying.length > 0 && ` · ${notPlaying.length} Can't Make It`}
                </span>
              )}
            </GWCardHeader>

            {showButtonsForCurrent ? (
              <>
              {totalPlayers > 0 && (
                <p style={{ fontSize: "0.85rem", color: "#888", margin: "0 0 0.75rem 0" }}>
                  {respondedCount}/{totalPlayers} have responded
                </p>
              )}
              <ButtonRow>
                <AvailBtn
                  $variant="available"
                  onClick={() => submitResponse(currentGW.id, "available")}
                >
                  Available
                </AvailBtn>
                <AvailBtn
                  $variant="unavailable"
                  onClick={() => submitResponse(currentGW.id, "unavailable")}
                >
                  Can't Make It
                </AvailBtn>
              </ButtonRow>
              </>
            ) : (
              <>
                <ResponseBadge>
                  <ResponseText $resp={myCurrentResponse}>
                    {myCurrentResponse === "available" ? "You're in!" : "You're out"}
                  </ResponseText>
                  <ChangeLink
                    onClick={() =>
                      setChangingGW((prev) => ({ ...prev, [currentGW.id]: true }))
                    }
                  >
                    Change
                  </ChangeLink>
                </ResponseBadge>

                {playing.length > 0 && (
                  <>
                    <MemberGroupTitle>Playing ({playing.length})</MemberGroupTitle>
                    <MemberList>
                      {playing.map((r) => (
                        <MemberItem key={r.key}>
                          <Dot $color="#4CAF50" />
                          {r.name}
                          {r.guest && <GuestTag>guest</GuestTag>}
                        </MemberItem>
                      ))}
                    </MemberList>
                  </>
                )}

                {notPlaying.length > 0 && (
                  <>
                    <MemberGroupTitle>Can't Make It ({notPlaying.length})</MemberGroupTitle>
                    <MemberList>
                      {notPlaying.map((r) => (
                        <MemberItem key={r.key}>
                          <Dot $color="#E53935" />
                          {r.name}
                          {r.guest && <GuestTag>guest</GuestTag>}
                        </MemberItem>
                      ))}
                    </MemberList>
                  </>
                )}

                {noResponse.length > 0 && (
                  <>
                    <MemberGroupTitle>No Response ({noResponse.length})</MemberGroupTitle>
                    <MemberList>
                      {noResponse.map((r) => (
                        <MemberItem key={r.key}>
                          <Dot $color="#cccccc" />
                          {r.name}
                          {r.guest && <GuestTag>guest</GuestTag>}
                        </MemberItem>
                      ))}
                    </MemberList>
                  </>
                )}
              </>
            )}
          </GWCard>

          {/* ── Upcoming GWs (calculated) ────────────────────────────────── */}
          {upcomingMatches.length > 0 && (
            <>
              <Divider />
              <SectionTitle>Upcoming</SectionTitle>

              {upcomingMatches.map(({ gwID, date, kickoffTime }) => {
                const gwResponses = upcomingResponses[gwID] || {};
                const myResp = gwResponses[uid];
                const showButtons = !myResp || changingGW[gwID];

                return (
                  <CompactCard key={gwID}>
                    <CompactHeader>
                      <CompactLeft>
                        <GWLabel>{gwID}</GWLabel>{" "}
                        <GWMeta>{formatShortDate(date)}{kickoffTime ? ` · ${kickoffTime}` : ""}</GWMeta>
                      </CompactLeft>

                      {showButtons ? (
                        <CompactButtonRow>
                          <SmallBtn
                            $variant="available"
                            onClick={() => submitResponse(gwID, "available")}
                          >
                            Available
                          </SmallBtn>
                          <SmallBtn
                            $variant="unavailable"
                            onClick={() => submitResponse(gwID, "unavailable")}
                          >
                            Can't Make It
                          </SmallBtn>
                        </CompactButtonRow>
                      ) : (
                        <ResponseBadge style={{ margin: 0 }}>
                          <ResponseText $resp={myResp} style={{ fontSize: "0.9rem" }}>
                            {myResp === "available" ? "You're in!" : "You're out"}
                          </ResponseText>
                          <ChangeLink
                            onClick={() =>
                              setChangingGW((prev) => ({ ...prev, [gwID]: true }))
                            }
                          >
                            Change
                          </ChangeLink>
                        </ResponseBadge>
                      )}
                    </CompactHeader>
                  </CompactCard>
                );
              })}
            </>
          )}
        </>
      )}
    </PageWrapper>
  );
}
